import React, { useState, useEffect } from "react";
import { FormattedMessage } from 'react-intl';
import { Paginator } from "../../layout/Paginator/Paginator";
import { TableComponent } from "./table/Table";
import { getTickets as getTicketsApi } from "../../../api/clients/tickets";
import {
  ACL,
  PAGE_CONTEXTS,
  TABLE_CONTEXTS,
  TABLE_SEARCHES_TYPES,
  TRANSLATED_TICKETS_STATUSES
} from '../../../config/constants';
import { FiltersList } from '../../shared/filters/FiltersList';
import { CheckboxFilter } from '../../shared/filters/CheckboxFilter';
import { useDidMountEffect } from '../../../helpers/useDidMountEffect';
import { store } from '../../../store/store';
import { hideNotification } from '../../../store/actions/notification';
import { AutoRefreshTable } from "../../layout/Table/AutoRefreshTable";
import { RowsPerPage } from "../../layout/Table/RowsPerPage";
import { tablesSearches as tablesSearchesAction } from "../../../store/actions/user";
import { useDispatch, useSelector } from "react-redux";
import { getTableSearches } from "../../../helpers/tables";
import { FILTERS, FILTERS_NAMES } from '../../../config/packageStatus';
import { ListToolbar } from '../../layout/Workspace';

let oneTimeCall = false;
function TicketsTableComponent() {
  const tablesSearches = getTableSearches(PAGE_CONTEXTS.TICKETS.LIST, TABLE_CONTEXTS.TICKETS);

  const [sortDirection, setSortDirection] = useState(tablesSearches.sortDirection);
  const [sortField, setSortField] = useState(tablesSearches.sortField);
  const [tickets, setTickets] = useState(null);
  const [meta, setMeta] = useState(tablesSearches.meta);
  const [filters, setFilters] = useState(tablesSearches.filters)
  const [rowsPerPage, setRowsPerPage] = useState(tablesSearches.rowsPerPage);
  const dispatch = useDispatch();
  const stateTablesSearches = useSelector(store => store.user.tablesSearches);

  useEffect(() => {
    getTickets( (meta && meta.current_page) ?? 1, sortField, sortDirection, null, filters );

    return () => {
      store.dispatch(hideNotification());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDidMountEffect( () => {
    if (! ACL.CLIENT.TICKETS.MANAGE.hasAccess()) {
      return;
    }

    getTickets( 1, null, null, null, filters );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ filters ] )

  useEffect( () => {
    getTickets( 1, sortField, sortDirection, null, filters );

    return () => {
      store.dispatch(hideNotification());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage] );


  /**
   * Get client tickets
   * @param {number} [page]
   * @param {string} [sortField]
   * @param {string} [sortDirection]
   * @param {string} [searchText]
   * @param {object} [filters]
   * @param {boolean} [hideNotification]
   */
  const getTickets = (page, sortField, sortDirection, searchText, filters, hideNotification = false) => {
    if (! ACL.CLIENT.TICKETS.MANAGE.hasAccess() || oneTimeCall === true) {
      return;
    }

    oneTimeCall = true;

    getTicketsApi({
      pagination: {
        page: page,
        sort_by: sortField,
        sort: sortDirection,
        q: searchText,
        per_page: rowsPerPage,
        statuses: filters?.byStatuses.map( function (item) {
          return item.identifier
        } ),
        page_context: PAGE_CONTEXTS.TICKETS.LIST,
      },
      hideNotification: hideNotification,
    }).then((response) => {
      if (!response || !response.data) {
        return;
      }

      setTickets(response.data);
      setMeta(response.meta);

      let newTablesSearches = stateTablesSearches ?? {};
      
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST] = {};
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST][TABLE_CONTEXTS.TICKETS] = {};
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST][TABLE_CONTEXTS.TICKETS][TABLE_SEARCHES_TYPES.SORT_FIELD] = sortField;
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST][TABLE_CONTEXTS.TICKETS][TABLE_SEARCHES_TYPES.SORT_DIRECTION] = sortDirection;
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST][TABLE_CONTEXTS.TICKETS][TABLE_SEARCHES_TYPES.META] = response.meta;
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST][TABLE_CONTEXTS.TICKETS][TABLE_SEARCHES_TYPES.FILTERS] = filters ?? { byStatuses: [] };
      newTablesSearches[PAGE_CONTEXTS.TICKETS.LIST][TABLE_CONTEXTS.TICKETS][TABLE_SEARCHES_TYPES.ROWS_PER_PAGE] = rowsPerPage;

      dispatch( tablesSearchesAction(newTablesSearches) );
    }).finally(() => {
      oneTimeCall = false;
    });
  };

  /**
   * Apply sort by appliedSortField
   * @param {string} appliedSortField
   */
  function applySort(appliedSortField) {
    let newSortDirection = "";

    if (sortField) {
      if (sortField === appliedSortField) {
        newSortDirection = sortDirection === "asc" ? "desc" : "asc";
      } else {
        newSortDirection = "asc";
      }
    } else {
      newSortDirection = "asc";
    }

    setSortField(appliedSortField);
    setSortDirection(newSortDirection);

    getTickets(meta.current_page, appliedSortField, newSortDirection, '', filters, true);
  }

  const applyFilters = (key, data) => {
    setFilters( {
      ...filters,
      [key]: data
    } );
  }

  const removeAllFilters = () => {
    setFilters( {
      byStatuses: [],
    } );
  }

  const removeFilter = item => {
    setFilters( {
      ...filters,
      [item.key]: filters[item.key].filter( elem => elem.identifier !== item.identifier )
    } );
  }

  return (
    <div className="tickets-list-layout">
      <ListToolbar
        activeFilters={(
          <FiltersList
            id="filters"
            filters={filters}
            filterList="tickets"
            onRemoveFilter={removeFilter}
            onRemoveAllFilters={removeAllFilters}
          />
        )}
        className="tickets-list-toolbar"
        filters={(
          <div className="tickets-list-filter">
            <span className="tickets-list-filter__label">
              <FormattedMessage id="components.pages.clients.tickets.table.status"/>
            </span>
            <CheckboxFilter
              index={0}
              applyFilters={applyFilters}
              filters={filters.byStatuses}
              filterKey={FILTERS.byStatuses}
              filterName={FILTERS_NAMES.byStatuses}
              list={TRANSLATED_TICKETS_STATUSES}
              type="button"
            />
          </div>
        )}
        settings={(
          <AutoRefreshTable
            id="tickets"
            getData={getTickets}
            sortDirection={sortDirection}
            sortField={sortField}
            filters={filters}
          />
        )}
      />
      <div className="tickets-list-table">
        <TableComponent
          applySort={(sortField) => applySort(sortField)}
          tickets={tickets}
          actionCallback={(useState) => {
            if (useState) {
              getTickets(meta.current_page, sortField, sortDirection, '');
            } else {
              getTickets();
            }
          }}
        />
      </div>
      <div className="tickets-list-pagination">
        <Paginator
          meta={meta}
          settings={(
            <RowsPerPage
              changeRowsPerPage={(rowsPerPage) => {
                setRowsPerPage(rowsPerPage);
              }}
              id="tickets"
              rowsPerPage={rowsPerPage}
            />
          )}
          changePage={(page) =>
            getTickets(page, sortField, sortDirection, '', filters, true)
          }
        />
      </div>
    </div>
  );
}

export { TicketsTableComponent };
